import { useCallback, useRef, useState } from "react";

const MAX_HISTORY = 80;

const clone = (o) => JSON.parse(JSON.stringify(o));

// Finding things inside a template.
export function findBand(t, bandId) {
  return (t.bands || []).find((b) => b.id === bandId) || null;
}
export function findElement(t, elId) {
  for (const b of t.bands || []) {
    const e = (b.elements || []).find((x) => x.id === elId);
    if (e) return { band: b, el: e };
  }
  return null;
}

/*
 * Template state with undo/redo.
 *   update(mutator, { key })  - mutator edits a draft copy; consecutive updates
 *                               with the same key (typing, dragging) merge into
 *                               one undo step.
 *   update(mutator, { transient: true }) - live preview while dragging; call
 *                               commitTransient() when the gesture ends.
 */
export function useTemplateState(initial) {
  const [template, setTemplate] = useState(initial);
  const [, force] = useState(0);
  const hist = useRef({ past: [], future: [], lastKey: null, lastTime: 0, transientBase: null });
  const current = useRef(initial);
  const dirty = useRef(false);

  const set = (next) => {
    current.current = next;
    setTemplate(next);
  };

  const update = useCallback((mutator, opts = {}) => {
    const h = hist.current;
    const base = current.current;
    const draft = clone(base);
    const res = mutator(draft);
    const next = res && typeof res === "object" && res !== draft && res.bands ? res : draft;
    if (opts.transient) {
      if (!h.transientBase) h.transientBase = base;
      set(next);
      return;
    }
    const now = Date.now();
    const merge = opts.key && opts.key === h.lastKey && now - h.lastTime < 1500;
    if (!merge) {
      h.past.push(base);
      if (h.past.length > MAX_HISTORY) h.past.shift();
    }
    h.future = [];
    h.lastKey = opts.key || null;
    h.lastTime = now;
    dirty.current = true;
    set(next);
  }, []);

  const commitTransient = useCallback(() => {
    const h = hist.current;
    if (!h.transientBase) return;
    h.past.push(h.transientBase);
    if (h.past.length > MAX_HISTORY) h.past.shift();
    h.future = [];
    h.transientBase = null;
    h.lastKey = null;
    dirty.current = true;
    force((x) => x + 1);
  }, []);

  const cancelTransient = useCallback(() => {
    const h = hist.current;
    if (!h.transientBase) return;
    set(h.transientBase);
    h.transientBase = null;
  }, []);

  const undo = useCallback(() => {
    const h = hist.current;
    if (!h.past.length) return;
    h.future.push(current.current);
    h.lastKey = null;
    dirty.current = true;
    set(h.past.pop());
  }, []);

  const redo = useCallback(() => {
    const h = hist.current;
    if (!h.future.length) return;
    h.past.push(current.current);
    h.lastKey = null;
    dirty.current = true;
    set(h.future.pop());
  }, []);

  const reset = useCallback((t) => {
    hist.current = { past: [], future: [], lastKey: null, lastTime: 0, transientBase: null };
    dirty.current = false;
    set(t);
  }, []);

  return {
    template,
    getTemplate: () => current.current,
    update,
    commitTransient,
    cancelTransient,
    undo,
    redo,
    reset,
    canUndo: hist.current.past.length > 0,
    canRedo: hist.current.future.length > 0,
    isDirty: () => dirty.current,
    markSaved: () => {
      dirty.current = false;
      force((x) => x + 1);
    },
  };
}
